import React from 'react';
import PageHero from '../components/PageHero';
import RevealOnScroll from '../components/RevealOnScroll';
import { AlertTriangle, Ban, Box, FileText, Info, ShieldAlert, Gavel } from 'lucide-react';

const RulesPage: React.FC = () => {
  return (
    <>
      <PageHero 
        title="Rules & Regulations"
        subtitle="Please review our shipping policies to ensure a smooth experience."
      />
      
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <RevealOnScroll>
            <div className="bg-red-50 border-l-4 border-red-500 p-4 md:p-6 rounded-r-xl mb-10 md:mb-12">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-6 h-6 text-red-600 shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-red-900 text-base md:text-lg">Please Read Carefully</h3>
                  <p className="text-red-700 mt-2 text-xs md:text-sm leading-relaxed">
                    Failure to comply with these rules and regulations may result in additional fees, shipment delays, 
                    confiscation of items by customs authorities, or termination of your account.
                  </p>
                </div>
              </div>
            </div>
          </RevealOnScroll>

          <div className="space-y-12 md:space-y-16">
            {/* Prohibited Items */}
            <RevealOnScroll>
              <div id="prohibited">
                <div className="flex items-center gap-3 mb-4 md:mb-6 border-b border-slate-200 pb-4">
                   <Ban className="w-6 h-6 md:w-8 md:h-8 text-brand-600" />
                   <h2 className="text-xl md:text-2xl font-extrabold text-slate-900">Prohibited Items</h2>
                </div>
                <p className="text-slate-600 mb-6 text-sm md:text-base">
                  The following items are strictly prohibited from shipping through our service. Attempting to ship these items may result in confiscation, destruction, penalties, and termination of your account.
                </p>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-slate-50 p-5 md:p-6 rounded-xl border border-slate-100">
                    <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                      <span className="w-2 h-2 bg-red-500 rounded-full"></span> Dangerous Goods
                    </h3>
                    <ul className="space-y-2 text-sm text-slate-600 list-disc list-inside marker:text-slate-400">
                      <li>Explosives and fireworks</li>
                      <li>Flammable materials</li>
                      <li>Compressed gases</li>
                      <li>Corrosive substances</li>
                      <li>Oxidizers and organic peroxides</li>
                      <li>Toxic and infectious substances</li>
                      <li>Radioactive materials</li>
                    </ul>
                  </div>

                  <div className="bg-slate-50 p-5 md:p-6 rounded-xl border border-slate-100">
                    <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                       <span className="w-2 h-2 bg-red-500 rounded-full"></span> Illegal Items
                    </h3>
                    <ul className="space-y-2 text-sm text-slate-600 list-disc list-inside marker:text-slate-400">
                      <li>Illegal drugs and narcotics</li>
                      <li>Prescription drugs without docs</li>
                      <li>Counterfeit goods or merchandise</li>
                      <li>Stolen property</li>
                      <li>Firearms, weapons and parts</li>
                      <li>Pornographic materials</li>
                      <li>Items violating IP rights</li>
                    </ul>
                  </div>

                  <div className="bg-slate-50 p-5 md:p-6 rounded-xl border border-slate-100 md:col-span-2">
                    <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                       <span className="w-2 h-2 bg-red-500 rounded-full"></span> Other Prohibited Items
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <ul className="space-y-2 text-sm text-slate-600 list-disc list-inside marker:text-slate-400">
                        <li>Human remains or body parts</li>
                        <li>Live animals</li>
                        <li>Perishable food items</li>
                        <li>Plants and seeds</li>
                      </ul>
                      <ul className="space-y-2 text-sm text-slate-600 list-disc list-inside marker:text-slate-400">
                         <li>Currency, cash, or traveler's checks</li>
                         <li>Lottery tickets or gambling devices</li>
                         <li>Items prohibited for import to Maldives</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </RevealOnScroll>

            {/* Restricted Items */}
            <RevealOnScroll>
               <div id="restricted" className="bg-orange-50 p-6 md:p-8 rounded-2xl border border-orange-100">
                  <div className="flex items-center gap-3 mb-4">
                     <ShieldAlert className="w-5 h-5 md:w-6 md:h-6 text-orange-600" />
                     <h2 className="text-lg md:text-xl font-bold text-slate-900">Restricted Items</h2>
                  </div>
                  <p className="text-slate-700 text-sm mb-6">
                    The following items have special restrictions and may require permits, licenses, or additional documentation. 
                    Please contact our customer service before shipping these items.
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                     {['Medications', 'Electronics & Batteries', 'High-Value Items', 'Food Products'].map((item, i) => (
                        <div key={i} className="bg-white p-3 rounded-lg text-center shadow-sm text-xs md:text-sm font-semibold text-slate-800">
                           {item}
                        </div>
                     ))}
                  </div>
               </div>
            </RevealOnScroll>

            {/* Packaging Requirements */}
            <RevealOnScroll>
              <div id="packaging">
                <div className="flex items-center gap-3 mb-4 md:mb-6 border-b border-slate-200 pb-4">
                   <Box className="w-6 h-6 md:w-8 md:h-8 text-brand-600" />
                   <h2 className="text-xl md:text-2xl font-extrabold text-slate-900">Packaging Requirements</h2>
                </div>
                <p className="text-slate-600 mb-6 text-sm md:text-base">
                  All packages must be properly packaged to ensure safe delivery. OTLExpress reserves the right to repackage items that are not properly packaged, for which additional fees may apply.
                </p>
                
                <div className="space-y-6">
                   <div>
                      <h3 className="font-bold text-slate-900 text-base md:text-lg mb-3">General Requirements</h3>
                      <ul className="grid md:grid-cols-2 gap-3">
                         {['Use strong, durable outer packaging', 'Cushion contents adequately', 'Seal packages securely', 'Remove old labels', 'No movement inside package', 'Withstand 4-foot drop test'].map((req, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                               <span className="text-brand-500 font-bold">•</span> {req}
                            </li>
                         ))}
                      </ul>
                   </div>
                   <div>
                      <h3 className="font-bold text-slate-900 text-base md:text-lg mb-3">Special Items Packaging</h3>
                      <ul className="space-y-2 text-sm text-slate-600">
                         <li className="flex flex-col sm:flex-row gap-1 sm:gap-2"><span className="font-semibold text-slate-900 min-w-[100px]">Fragile:</span> Double-boxed with padding between boxes.</li>
                         <li className="flex flex-col sm:flex-row gap-1 sm:gap-2"><span className="font-semibold text-slate-900 min-w-[100px]">Electronics:</span> Original packaging when possible.</li>
                         <li className="flex flex-col sm:flex-row gap-1 sm:gap-2"><span className="font-semibold text-slate-900 min-w-[100px]">Liquids:</span> Sealed in leak-proof containers with absorbent materials.</li>
                         <li className="flex flex-col sm:flex-row gap-1 sm:gap-2"><span className="font-semibold text-slate-900 min-w-[100px]">Sharp:</span> Securely wrapped to prevent puncturing.</li>
                      </ul>
                   </div>
                </div>
              </div>
            </RevealOnScroll>

            {/* Customs & Duties */}
            <RevealOnScroll>
              <div id="customs">
                <div className="flex items-center gap-3 mb-4 md:mb-6 border-b border-slate-200 pb-4">
                   <FileText className="w-6 h-6 md:w-8 md:h-8 text-brand-600" />
                   <h2 className="text-xl md:text-2xl font-extrabold text-slate-900">Customs & Duties</h2>
                </div>
                
                <div className="prose prose-slate max-w-none text-slate-600 text-sm">
                   <p className="mb-4">
                      All international shipments are subject to customs clearance procedures. Understanding customs requirements is essential for smooth international shipping.
                   </p>
                   
                   <div className="grid md:grid-cols-2 gap-8 mt-6">
                      <div>
                         <h4 className="font-bold text-slate-900 mb-2">Customs Clearance Process</h4>
                         <ul className="list-disc list-inside space-y-1">
                            <li>All shipments subject to inspection</li>
                            <li>Accurate declaration required by law</li>
                            <li>We provide necessary documentation</li>
                            <li>Processing time varies by country</li>
                         </ul>
                      </div>
                      <div>
                         <h4 className="font-bold text-slate-900 mb-2">Duties and Taxes</h4>
                         <ul className="list-disc list-inside space-y-1">
                            <li>Recipient is responsible for all fees</li>
                            <li>Calculated based on value & origin</li>
                            <li>Undervaluing items is illegal</li>
                            <li>De minimis thresholds may apply</li>
                         </ul>
                      </div>
                   </div>
                   
                   <div className="mt-6 bg-blue-50 p-4 rounded-lg border border-blue-100">
                      <p className="text-blue-800 font-medium text-xs">
                         <strong>Important:</strong> OTLExpress cannot guarantee customs clearance times. For accurate duty/tax info, please consult the Maldives Customs Service.
                      </p>
                   </div>
                </div>
              </div>
            </RevealOnScroll>

             {/* Footer Policy Notes */}
             <RevealOnScroll>
                <div className="border-t border-slate-200 pt-8 mt-8">
                   <div className="grid md:grid-cols-2 gap-8">
                      <div>
                         <h3 className="font-bold text-slate-900 mb-2 flex items-center gap-2"><Gavel className="w-4 h-4"/> Service Termination</h3>
                         <p className="text-xs text-slate-500 leading-relaxed mb-4">
                            OTLExpress reserves the right to terminate services for violations including shipping prohibited items, fraud, or abusive behavior.
                         </p>
                      </div>
                      <div>
                         <h3 className="font-bold text-slate-900 mb-2 flex items-center gap-2"><Info className="w-4 h-4"/> Policy Updates</h3>
                         <p className="text-xs text-slate-500 leading-relaxed">
                            We may update these rules periodically. Continued use of our services constitutes acceptance of updated policies.
                         </p>
                         <p className="text-xs text-slate-400 mt-2">Last updated: June 1, 2025</p>
                      </div>
                   </div>
                </div>
             </RevealOnScroll>

          </div>
        </div>
      </section>
    </>
  );
};

export default RulesPage;